import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSortable, SortableOptions } from 'react-sortablejs';
import { SortableEvent } from 'sortablejs';
import { EmptyListActions } from '~/components/ui/EmptyListActions';
import { Result } from '~/components/ui/Result';
import { useSortJobMutation } from '~/generated/graphql';
import { ListJobsQuery } from '~/gql/graphql';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { JobListItem } from './JobListItem';

type Job = Extract<ListJobsQuery['jobBoard'][number], { __typename: 'Job' }>;

type Props = {
  jobs?: ListJobsQuery['jobBoard'];
  scrollId?: string;
  hasFilters?: boolean;
  isSmall: boolean;
  selected: string[];
  toggleSelected: (id: string, shift: boolean, single?: boolean) => void;
};

const options: SortableOptions = {
  animation: 200,
  ghostClass: 'opacity-50',
  chosenClass: 'opacity-100',
  dragClass: 'opacity-100',

  // these two rules effectively disables dragging on mobile
  delay: 2000,
  delayOnTouchOnly: true,
};

export const Unscheduled = memo(
  function Unscheduled({
    jobs,
    scrollId,
    hasFilters,
    isSmall,
    selected,
    toggleSelected,
  }: Props) {
    const [res, sort] = useSortJobMutation();
    const [state, setState] = useState(() => (jobs as Job[]) ?? []);
    const { t } = useTranslation();
    const { fetching } = res;
    const { isMobile } = useBreakpoint();
    const containerRef = useRef<HTMLDivElement>(null);

    // Mobile-only scroll reset on mount
    useEffect(() => {
      if (isMobile) {
        // Reset scroll position for mobile
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    }, [isMobile]); // Only run when isMobile changes or on mount

    // Mobile-only scroll guard to prevent unwanted scrolling from memorized position in Scheduled view
    useEffect(() => {
      if (!isMobile) return;

      let scrollGuardActive = true;
      let scrollResetAttempts = 0;

      const mobileScrollGuard = () => {
        // Mobile-only scroll guard to prevent unwanted scrolling
        if (scrollGuardActive && window.scrollY > 0) {
          scrollResetAttempts++;

          // Single immediate scroll reset
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

          // Only add one delayed reset for the first few attempts
          if (scrollResetAttempts <= 3) {
            setTimeout(
              () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }),
              50
            );
          }
        }
      };

      // Reduced guard time - 1 second for mobile
      const guardDuration = 1000;
      const guardTimeout = setTimeout(() => {
        scrollGuardActive = false;
      }, guardDuration);

      // Add mobile scroll guard
      window.addEventListener('scroll', mobileScrollGuard, { passive: true });

      return () => {
        clearTimeout(guardTimeout);
        scrollGuardActive = false;
        window.removeEventListener('scroll', mobileScrollGuard);
      };
    }, [isMobile]);

    // Stabilize jobs dependency with proper comparison
    useEffect(() => {
      const newJobs = (jobs as Job[]) ?? [];

      // Only update state if the jobs have actually changed
      setState((prevState) => {
        // Quick length check first
        if (prevState.length !== newJobs.length) {
          return newJobs;
        }

        // If lengths are the same, check if job IDs and content actually changed
        const hasChanged = prevState.some((prevJob, index) => {
          const newJob = newJobs[index];
          return (
            !newJob || prevJob.id !== newJob.id || prevJob.name !== newJob.name
          );
        });

        if (hasChanged) {
          return newJobs;
        }

        return prevState;
      });
    }, [jobs]);

    // Handle scrollId for scrolling to specific jobs (e.g., newly created jobs)
    useEffect(() => {
      if (scrollId && state && state.length > 0) {
        // Check if the scrollId corresponds to a job in our current list
        const jobExists = state.find((job) => job.id === scrollId);
        if (jobExists) {
          // Use a small delay to ensure the component is fully rendered
          setTimeout(() => {
            const jobElement = document.querySelector(
              `a[href="/jobs/${scrollId}"]`
            );
            if (jobElement) {
              jobElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 100);
        }
      }
    }, [scrollId, state]);

    // Memoized update function to prevent recreation on every render
    const update = useCallback(
      async ({ newIndex, oldIndex }: SortableEvent) => {
        if (newIndex == null || oldIndex == null) {
          return;
        }

        setState((currentState) => {
          const idx = newIndex > oldIndex ? newIndex + 1 : newIndex;
          const place =
            idx === currentState.length
              ? { sort: 'after', idx: idx - 1 }
              : { sort: 'before', idx };
          const job = currentState[place.idx];

          // Perform the sort mutation
          sort({
            id: currentState[oldIndex].id,
            sort: place.sort,
            rel: job.id,
          });

          return currentState; // State will be updated by the mutation response
        });
      },
      [sort]
    );

    if (!state) {
      return null;
    }

    if (jobs?.length === 0) {
      if (hasFilters) {
        return (
          <div className='max-w-full flex-1 overflow-x-hidden px-7 py-4'>
            There are no jobs matching these filters...
          </div>
        );
      } else {
        return <EmptyListActions listType='Job' />;
      }
    }

    const isAllSelected = jobs?.length === selected?.length;

    return (
      <>
        <div
          ref={containerRef}
          className='max-w-full flex-1 gap-3 overflow-x-hidden bg-surface-container-background p-3 pb-[25vh] pt-2 lg:px-4 lg:pb-6 lg:h-screen-minus-nav'
        >
          <div className='pb-2.5 pt-1'>
            <Result count={state?.length} />
          </div>
          <ReactSortable
            list={state}
            setList={(newList) => {
              // Only update if the list actually changed to prevent unnecessary re-renders
              setState((currentState) => {
                if (currentState.length !== newList.length) {
                  return newList;
                }
                const hasChanged = currentState.some(
                  (item, index) => item.id !== newList[index]?.id
                );
                return hasChanged ? newList : currentState;
              });
            }}
            onStart={(evt) => {
              evt?.preventDefault();
              return false;
            }}
            onUpdate={update}
            {...options}
          >
            {state.map((job) => (
              <JobListItem
                key={job.id}
                small={isSmall}
                job={job}
                selected={isAllSelected || selected.includes(job.id)}
                onSelect={(_, shift, single) =>
                  toggleSelected(job.id, shift, single)
                }
              />
            ))}
          </ReactSortable>
        </div>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    // Check if jobs array has actually changed content-wise, not just reference
    const jobsEqual =
      prevProps.jobs === nextProps.jobs ||
      (!!prevProps.jobs &&
        !!nextProps.jobs &&
        prevProps.jobs.length === nextProps.jobs.length &&
        prevProps.jobs.every(
          (job, index) => job.id === nextProps.jobs?.[index]?.id
        ));

    const selectedEqual =
      prevProps.selected === nextProps.selected ||
      (prevProps.selected.length === nextProps.selected.length &&
        prevProps.selected.every(
          (id, index) => id === nextProps.selected[index]
        ));

    return (
      jobsEqual &&
      prevProps.scrollId === nextProps.scrollId &&
      prevProps.hasFilters === nextProps.hasFilters &&
      prevProps.isSmall === nextProps.isSmall &&
      selectedEqual &&
      prevProps.toggleSelected === nextProps.toggleSelected
    );
  }
);
